import { useState, useMemo, useEffect } from 'react';
import toast from 'react-hot-toast';
import { X, Merge, SplitSquareHorizontal, ArrowRight, AlertTriangle } from 'lucide-react';
import { useClientIdentityTools } from '../../hooks/useClientIdentityTools';
import { useClientDetail } from '../../hooks/useClientDetail';
import type { ClientListItem } from '../../hooks/useClients';

type Mode = 'merge' | 'split';

interface ClientIdentityModalProps {
  companyId?: string;
  /** The roster currently visible to the operator (RBAC-scoped) — merge candidates. */
  clients: ClientListItem[];
  /** When opened from an ambiguous client, pre-seed split mode on that client. */
  initialMode?: Mode;
  initialClientId?: string | null;
  onClose: () => void;
  /** Refresh the roster after a successful mutation. */
  onChanged?: () => void;
}

const labelClass = 'block text-[11px] uppercase tracking-wide text-gray-500 font-bold mb-1';
const selectClass =
  'w-full bg-gray-900 border border-gray-700 rounded px-2 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500';

export function ClientIdentityModal({
  companyId,
  clients,
  initialMode = 'merge',
  initialClientId = null,
  onClose,
  onChanged,
}: ClientIdentityModalProps) {
  const { mergeClients, splitClient, busy } = useClientIdentityTools(companyId);
  const [mode, setMode] = useState<Mode>(initialMode);

  // Merge state.
  const [survivorId, setSurvivorId] = useState('');
  const [mergedId, setMergedId] = useState('');

  // Split state.
  const [splitSourceId, setSplitSourceId] = useState<string | null>(initialClientId);
  const [selectedLoanIds, setSelectedLoanIds] = useState<string[]>([]);
  const { client: splitSource, loading: splitLoading } = useClientDetail(
    companyId,
    mode === 'split' ? splitSourceId : null,
  );

  // Reset selected loans whenever the split source changes.
  useEffect(() => {
    setSelectedLoanIds([]);
  }, [splitSourceId]);

  const sortedClients = useMemo(
    () => [...clients].sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [clients],
  );

  const mergeValid = !!survivorId && !!mergedId && survivorId !== mergedId;
  // Anchor = the earliest selected loan id (deterministic, matches deriveSplitClientId intent).
  const clusterAnchorLoanId = useMemo(
    () => (selectedLoanIds.length ? [...selectedLoanIds].sort()[0] : ''),
    [selectedLoanIds],
  );
  const totalSourceLoans = splitSource?.loanIds.length ?? 0;
  const splitValid =
    !!splitSourceId &&
    selectedLoanIds.length > 0 &&
    selectedLoanIds.length < totalSourceLoans && // must leave at least one on the primary
    !!clusterAnchorLoanId;

  const runMerge = async () => {
    if (!mergeValid) return;
    try {
      const res = await mergeClients(survivorId, mergedId);
      if (res.alreadyMerged) {
        toast('Clients were already merged — no changes made.');
      } else {
        toast.success(
          `Merged. ${res.eventsRepointed ?? 0} event(s) re-pointed, ${res.contactsMoved ?? 0} contact(s) moved.`,
        );
      }
      onChanged?.();
      onClose();
    } catch (e) {
      console.error('[mergeClients] failed:', e);
      toast.error(e instanceof Error ? e.message : 'Merge failed. Please try again.');
    }
  };

  const runSplit = async () => {
    if (!splitValid || !splitSourceId) return;
    try {
      const res = await splitClient({
        clientId: splitSourceId,
        clusterAnchorLoanId,
        loanIds: selectedLoanIds,
        resolvesAmbiguity: !!splitSource?.ambiguousIdentity,
      });
      if (res.alreadySplit) {
        toast('This split already exists — no changes made.');
      } else {
        toast.success(`Split out ${res.movedLoanCount ?? selectedLoanIds.length} loan(s) into a new client.`);
      }
      onChanged?.();
      onClose();
    } catch (e) {
      console.error('[splitClient] failed:', e);
      toast.error(e instanceof Error ? e.message : 'Split failed. Please try again.');
    }
  };

  const toggleLoan = (loanId: string) =>
    setSelectedLoanIds((prev) =>
      prev.includes(loanId) ? prev.filter((id) => id !== loanId) : [...prev, loanId],
    );

  return (
    <>
      <div className="fixed inset-0 bg-black/60 z-50" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div className="pointer-events-auto w-full max-w-xl bg-gray-800 border border-gray-700 rounded-xl shadow-2xl overflow-hidden">
          <div className="flex items-center gap-3 p-5 border-b border-gray-700">
            <div className="flex-1">
              <div className="text-lg font-bold text-white">Resolve client identity</div>
              <div className="text-xs text-gray-400">Merge two clients, or split one that holds two people.</div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded bg-gray-900 border border-gray-700 text-gray-400 flex items-center justify-center hover:text-white"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Mode toggle */}
          <div className="flex gap-2 p-4 border-b border-gray-700">
            <button
              onClick={() => setMode('merge')}
              className={`flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold border transition-colors ${
                mode === 'merge'
                  ? 'bg-blue-600/20 border-blue-500/40 text-blue-300'
                  : 'bg-gray-900 border-gray-700 text-gray-400 hover:text-white'
              }`}
            >
              <Merge className="w-4 h-4" /> Merge
            </button>
            <button
              onClick={() => setMode('split')}
              className={`flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold border transition-colors ${
                mode === 'split'
                  ? 'bg-blue-600/20 border-blue-500/40 text-blue-300'
                  : 'bg-gray-900 border-gray-700 text-gray-400 hover:text-white'
              }`}
            >
              <SplitSquareHorizontal className="w-4 h-4" /> Split
            </button>
          </div>

          <div className="p-5 space-y-4">
            {mode === 'merge' ? (
              <>
                <div className="bg-amber-900/20 border border-amber-500/30 text-amber-200 rounded-lg p-3 text-xs flex gap-2">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>
                    The survivor keeps its history and care timeline; the other client&apos;s loans, events and
                    contacts fold into it and it is marked merged. This cannot be undone from here.
                  </span>
                </div>
                <div>
                  <label className={labelClass}>Survivor (keeps history)</label>
                  <select className={selectClass} value={survivorId} onChange={(e) => setSurvivorId(e.target.value)}>
                    <option value="">Select the client to keep…</option>
                    {sortedClients.map((c) => (
                      <option key={c.clientId} value={c.clientId}>
                        {c.displayName} · {c.loanCount} loan(s){c.primaryBrokerDisplay ? ` · ${c.primaryBrokerDisplay}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex justify-center text-gray-500">
                  <ArrowRight className="w-4 h-4 rotate-90" />
                </div>
                <div>
                  <label className={labelClass}>Merge into survivor (folded in)</label>
                  <select className={selectClass} value={mergedId} onChange={(e) => setMergedId(e.target.value)}>
                    <option value="">Select the duplicate to fold in…</option>
                    {sortedClients
                      .filter((c) => c.clientId !== survivorId)
                      .map((c) => (
                        <option key={c.clientId} value={c.clientId}>
                          {c.displayName} · {c.loanCount} loan(s){c.primaryBrokerDisplay ? ` · ${c.primaryBrokerDisplay}` : ''}
                        </option>
                      ))}
                  </select>
                </div>
                <button
                  onClick={runMerge}
                  disabled={!mergeValid || busy}
                  className="w-full px-3 py-2.5 rounded-lg bg-gradient-to-r from-blue-600 to-blue-500 text-white text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-110"
                >
                  {busy ? 'Merging…' : 'Merge clients'}
                </button>
              </>
            ) : (
              <>
                <div>
                  <label className={labelClass}>Client to split</label>
                  <select
                    className={selectClass}
                    value={splitSourceId ?? ''}
                    onChange={(e) => setSplitSourceId(e.target.value || null)}
                  >
                    <option value="">Select the client that holds two people…</option>
                    {sortedClients.map((c) => (
                      <option key={c.clientId} value={c.clientId}>
                        {c.displayName} · {c.loanCount} loan(s)
                      </option>
                    ))}
                  </select>
                </div>

                {splitSourceId && (
                  <div>
                    <label className={labelClass}>
                      Loans to peel off into a new client ({selectedLoanIds.length} selected)
                    </label>
                    {splitLoading ? (
                      <div className="text-sm text-gray-500 py-3">Loading loans…</div>
                    ) : totalSourceLoans === 0 ? (
                      <div className="text-sm text-gray-500 py-3">This client has no loans to split.</div>
                    ) : (
                      <div className="max-h-52 overflow-auto rounded-lg border border-gray-700 divide-y divide-gray-700/60">
                        {(splitSource?.loanIds ?? []).map((loanId) => (
                          <label
                            key={loanId}
                            className="flex items-center gap-3 px-3 py-2 text-sm text-gray-200 hover:bg-gray-700/40 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={selectedLoanIds.includes(loanId)}
                              onChange={() => toggleLoan(loanId)}
                              className="accent-blue-500"
                            />
                            <span className="font-mono text-xs truncate">{loanId}</span>
                          </label>
                        ))}
                      </div>
                    )}
                    {selectedLoanIds.length >= totalSourceLoans && totalSourceLoans > 0 && (
                      <div className="text-xs text-amber-300 mt-2">
                        At least one loan must remain on the original client.
                      </div>
                    )}
                  </div>
                )}

                <button
                  onClick={runSplit}
                  disabled={!splitValid || busy}
                  className="w-full px-3 py-2.5 rounded-lg bg-gradient-to-r from-blue-600 to-blue-500 text-white text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-110"
                >
                  {busy ? 'Splitting…' : 'Split into a new client'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
